import { createContext, memo, useContext, type JSX } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AsyncState } from '@/components/async';
import { StatusBadge, type BadgeTone } from '@/components/dashboard/StatusBadge';
import { PillButton } from '@/components/entity/PillButton';
import { useDashboardUser } from '@/routes/dashboard/DashboardRouter';
import { useDashboardPropertyDetail, useDashboardUnitRow } from '@/hooks/useDashboardProperties';
import { PUBLIC_READINESS_LABELS } from '@/components/public/properties';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { UnitCreateDrawer, UnitEditDrawer, UnitPublishToggle } from '@/components/dashboard/units';
import type { UnitAvailabilityStatus, UnitSummary } from '@/lib/types/property';
import {
  formatFloorLevelLabel,
  formatMoveInTimingLabel,
  formatPropertyAddress,
  formatUnitLabel,
} from '@/lib/utils/propertyFormatting';

type UnitSummaryMap = Map<string, UnitSummary>;

const UnitListContext = createContext<UnitSummaryMap | null>(null);

function useUnitSummarySelector(unitId: string): UnitSummary {
  const map = useContext(UnitListContext);
  if (!map) {
    throw new Error('useUnitSummarySelector must be used within UnitListContext');
  }
  const summary = map.get(unitId);
  if (!summary) {
    throw new Error(`Unit summary not found for id ${unitId}`);
  }
  return summary;
}

export function PropertyUnitManager(): JSX.Element {
  const { propertyId } = useParams<{ propertyId: string }>();
  const navigate = useNavigate();
  const user = useDashboardUser();
  const desktop = useMediaQuery('(min-width: 1280px)');
  const tablet = useMediaQuery('(min-width: 768px)');
  const layout: UnitManagerLayout = desktop ? 'desktop' : tablet ? 'tablet' : 'mobile';

  const detailQuery = useDashboardPropertyDetail({
    user,
    propertyId: propertyId ?? null,
  });
  const isDetailLoading = detailQuery.status === 'pending';

  return (
    <section className="flex flex-col gap-xl py-8">
      <button
        type="button"
        onClick={() => {
          navigate('/dashboard/properties');
        }}
        className="min-h-11 w-fit rounded-full border border-brand-primary/40 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-brand-primary transition hover:bg-brand-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
      >
        Back to properties
      </button>

      <AsyncState
        data={detailQuery.data ?? null}
        isLoading={isDetailLoading}
        error={detailQuery.error}
        skeleton={<DetailSkeleton />}
      >
        {(detail) => {
          const resolvedPropertyId = detail.property.id ?? detail.summary.id;
          const units = detail.units ?? [];
          const unitMap: UnitSummaryMap = new Map(units.map((unit) => [unit.id, unit]));
          const unitIds = units.map((unit) => unit.id);

          return (
            <UnitListContext.Provider value={unitMap}>
              <header className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
                <div className="flex flex-col gap-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-brand-primary/70">
                    Unit manager
                  </p>
                  <h1 className="text-3xl font-semibold text-text-primary">
                    {formatPropertyAddress(detail.property.address)}
                  </h1>
                  <p className="text-sm text-text-primary/70">{detail.property.marketingSummary}</p>
                  <div className="flex flex-wrap gap-2">
                    {units.slice(0, 3).map((unit) => (
                      <StatusBadge
                        key={unit.id}
                        tone={readinessTone(unit.availability.status)}
                        label={`${formatUnitLabel(unit)}: ${PUBLIC_READINESS_LABELS[unit.availability.status]}`}
                      />
                    ))}
                  </div>
                </div>
                <div className="flex flex-wrap gap-3">
                  <UnitCreateDrawer
                    propertyId={resolvedPropertyId}
                    layout={layout}
                    trigger={<PillButton>Add unit</PillButton>}
                  />
                  <PillButton variant="secondary" disabled title="Bulk edits coming soon">
                    Bulk edit
                  </PillButton>
                </div>
              </header>
              {layout === 'desktop' ? (
                <UnitTable unitIds={unitIds} propertyId={resolvedPropertyId} layout={layout} />
              ) : (
                <UnitCardList unitIds={unitIds} propertyId={resolvedPropertyId} layout={layout} />
              )}
            </UnitListContext.Provider>
          );
        }}
      </AsyncState>
    </section>
  );
}

type UnitManagerLayout = 'desktop' | 'tablet' | 'mobile';

function UnitTable({
  unitIds,
  propertyId,
  layout,
}: {
  unitIds: string[];
  propertyId: string;
  layout: UnitManagerLayout;
}): JSX.Element {
  if (unitIds.length === 0) {
    return (
      <div className="bg-surface-muted/50 rounded-3xl border border-dashed border-surface-subtle p-8 text-center text-sm text-text-primary/70">
        No units yet. Use “Add unit” to create the first record for this property.
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-3xl border border-surface-subtle bg-white shadow-sm">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-surface-subtle text-left text-sm">
          <thead className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
            <tr>
              <th scope="col" className="px-4 py-3">
                Unit
              </th>
              <th scope="col" className="px-4 py-3">
                Area
              </th>
              <th scope="col" className="px-4 py-3">
                Net rent
              </th>
              <th scope="col" className="px-4 py-3">
                Move-in readiness
              </th>
              <th scope="col" className="px-4 py-3">
                Availability status
              </th>
              <th scope="col" className="px-4 py-3">
                Display on public website?
              </th>
              <th scope="col" className="px-4 py-3">
                Realtor outreach emails
              </th>
              <th scope="col" className="px-4 py-3">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-surface-subtle/80">
            {unitIds.map((unitId) => (
              <UnitTableRow key={unitId} propertyId={propertyId} layout={layout} unitId={unitId} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const UnitTableRow = memo(function UnitTableRow({
  unitId,
  propertyId,
  layout,
}: {
  unitId: string;
  propertyId: string;
  layout: UnitManagerLayout;
}): JSX.Element {
  const user = useDashboardUser();
  const navigate = useNavigate();
  const baseUnit = useUnitSummarySelector(unitId);
  const {
    data: rowData,
    isFetching,
    isLoading,
  } = useDashboardUnitRow({
    user,
    propertyId,
    unit: baseUnit,
  });
  const summary = rowData ?? baseUnit;
  const isRefreshing = isFetching && !isLoading;
  const floorLabel = formatFloorLevelLabel(summary.floorLevel, { emptyLabel: '' });

  const openUnitDetail = () => {
    navigate(`/dashboard/properties/${propertyId}/units/${unitId}`);
  };

  const handleRowClick = (event: React.MouseEvent<HTMLTableRowElement>) => {
    const target = event.target as HTMLElement | null;
    if (target?.closest('button, a, input, textarea, select')) {
      return;
    }

    openUnitDetail();
  };

  const handleRowKeyDown = (event: React.KeyboardEvent<HTMLTableRowElement>) => {
    const target = event.target as HTMLElement | null;
    if (target?.closest('button, a, input, textarea, select')) {
      return;
    }

    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    openUnitDetail();
  };

  return (
    <tr
      className="relative cursor-pointer align-top text-sm text-text-primary hover:bg-brand-primary/5"
      aria-busy={isRefreshing}
      data-row-refreshing={isRefreshing ? 'true' : 'false'}
      tabIndex={0}
      role="link"
      aria-label={`View unit ${formatUnitLabel(summary)}`}
      onClick={handleRowClick}
      onKeyDown={handleRowKeyDown}
    >
      <td className="px-4 py-4">
        {isRefreshing ? <RowLoadingOverlay /> : null}
        <div className="flex flex-col gap-1">
          <span className="text-sm font-semibold text-text-primary">
            {formatUnitLabel(summary)}
          </span>
          {floorLabel.length > 0 ? (
            <span className="text-xs text-text-primary/70">{floorLabel}</span>
          ) : null}
        </div>
      </td>
      <td className="px-4 py-4">{`${formatNumber(summary.areaSf)} SF`}</td>
      <td className="px-4 py-4">{formatRent(summary.rent.netRentPerSf)}</td>
      <td className="px-4 py-4">
        <span>{formatMoveInReadiness(summary)}</span>
      </td>
      <td className="px-4 py-4">
        <StatusBadge
          tone={readinessTone(summary.availability.status)}
          label={PUBLIC_READINESS_LABELS[summary.availability.status]}
        />
      </td>
      <td className="px-4 py-4">
        <StatusBadge
          tone={summary.displayOnPublicWebsite ? 'success' : 'neutral'}
          label={summary.displayOnPublicWebsite ? 'Yes' : 'No'}
        />
      </td>
      <td className="px-4 py-4">
        <UnitPublishToggle propertyId={propertyId} unit={summary} size="sm" />
      </td>
      <td className="px-4 py-4">
        <div className="flex flex-wrap gap-2">
          <UnitEditDrawer
            propertyId={propertyId}
            unit={summary}
            layout={layout}
            trigger={<PillButton size="sm">Edit</PillButton>}
          />
        </div>
      </td>
    </tr>
  );
});

function UnitCardList({
  unitIds,
  propertyId,
  layout,
}: {
  unitIds: string[];
  propertyId: string;
  layout: UnitManagerLayout;
}): JSX.Element {
  if (unitIds.length === 0) {
    return (
      <div className="bg-surface-muted/40 rounded-3xl border border-dashed border-surface-subtle p-8 text-center text-sm text-text-primary/70">
        No units yet. Use “Add unit” to create the first record for this property.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {unitIds.map((unitId) => (
        <UnitCard key={unitId} unitId={unitId} propertyId={propertyId} layout={layout} />
      ))}
      {layout === 'mobile' ? (
        <div className="sticky bottom-3 z-10 rounded-2xl border border-brand-primary/20 bg-white/95 p-4 shadow-lg backdrop-blur">
          <UnitCreateDrawer
            propertyId={propertyId}
            layout={layout}
            trigger={<PillButton className="w-full">Add unit</PillButton>}
          />
        </div>
      ) : null}
    </div>
  );
}

const UnitCard = memo(function UnitCard({
  unitId,
  propertyId,
  layout,
}: {
  unitId: string;
  propertyId: string;
  layout: UnitManagerLayout;
}): JSX.Element {
  const user = useDashboardUser();
  const baseUnit = useUnitSummarySelector(unitId);
  const {
    data: rowData,
    isFetching,
    isLoading,
  } = useDashboardUnitRow({
    user,
    propertyId,
    unit: baseUnit,
  });
  const summary = rowData ?? baseUnit;
  const readinessLabel = PUBLIC_READINESS_LABELS[summary.availability.status];
  const isRefreshing = isFetching && !isLoading;

  const actionGroup = (
    <div className="mt-4 flex flex-col gap-2 sm:flex-row">
      <UnitEditDrawer
        propertyId={propertyId}
        unit={summary}
        layout={layout}
        trigger={<PillButton>Edit unit</PillButton>}
      />
      <UnitPublishToggle propertyId={propertyId} unit={summary} fullWidth size="sm" />
    </div>
  );

  return (
    <article className="relative rounded-3xl border border-surface-subtle bg-white p-4 shadow-sm">
      {isRefreshing ? <RowLoadingOverlay /> : null}
      <div className="mb-4 overflow-hidden rounded-2xl border border-surface-subtle bg-surface-subtle">
        {typeof summary.heroImageUrl === 'string' && summary.heroImageUrl.length > 0 ? (
          <img
            src={summary.heroImageUrl}
            alt={`Hero image for unit ${formatUnitLabel(summary)}`}
            className="h-40 w-full object-cover"
          />
        ) : (
          <div
            className="h-40 bg-gradient-to-br from-brand-primary/10 via-white to-brand-accent/10"
            aria-hidden="true"
          />
        )}
      </div>
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">Unit</p>
          <p className="text-xl font-semibold text-text-primary">{formatUnitLabel(summary)}</p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-text-primary/60">
            Availability status
          </p>
          <StatusBadge tone={readinessTone(summary.availability.status)} label={readinessLabel} />
        </div>
      </div>
      <dl className="mt-4 grid gap-3 text-sm text-text-primary sm:grid-cols-2">
        <div>
          <dt className="text-xs uppercase tracking-wide text-text-primary/60">Area</dt>
          <dd className="mt-1 font-semibold">{formatNumber(summary.areaSf)} SF</dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wide text-text-primary/60">
            Move-in readiness
          </dt>
          <dd className="mt-1 font-semibold">{formatMoveInReadiness(summary)}</dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wide text-text-primary/60">Net rent</dt>
          <dd className="mt-1 font-semibold">{formatRent(summary.rent.netRentPerSf)}</dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wide text-text-primary/60">
            Display on public website?
          </dt>
          <dd className="mt-1 font-semibold">{summary.displayOnPublicWebsite ? 'Yes' : 'No'}</dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wide text-text-primary/60">
            Realtor outreach emails
          </dt>
          <dd className="mt-1 font-semibold">{summary.published ? 'Yes' : 'No'}</dd>
        </div>
      </dl>
      {actionGroup}
    </article>
  );
});

function RowLoadingOverlay(): JSX.Element {
  return (
    <div
      className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-white/80 backdrop-blur-sm"
      data-testid="row-loading-indicator"
      aria-label="Refreshing row"
    >
      <span className="h-5 w-5 animate-spin rounded-full border-2 border-brand-primary/40 border-t-brand-primary" />
    </div>
  );
}

function DetailSkeleton(): JSX.Element {
  return (
    <div className="flex flex-col gap-6">
      <div className="bg-surface-muted h-28 animate-pulse rounded-3xl border border-surface-subtle" />
      <div className="grid gap-4 md:grid-cols-2">
        {Array.from({ length: 2 }).map((_item, index) => (
          <div
            key={index}
            className="bg-surface-muted h-64 animate-pulse rounded-3xl border border-surface-subtle"
          />
        ))}
      </div>
    </div>
  );
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-CA', { maximumFractionDigits: 0 }).format(value);
}

function formatRent(value: number): string {
  const price = new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 2,
  }).format(value);
  return `${price}/SF Annual`;
}

function formatMoveInReadiness(unit: UnitSummary): string {
  return formatMoveInTimingLabel(unit.availabilityStart, unit.possessionTimeline);
}

function readinessTone(status: UnitAvailabilityStatus): BadgeTone {
  switch (status) {
    case 'available':
      return 'success';
    case 'unavailable':
      return 'warning';
    default:
      return 'neutral';
  }
}
